"use client";

import type { ReactNode } from "react";
import { useState } from "react";
import {
  Controller,
  useFormContext,
  type ControllerProps,
} from "react-hook-form";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import Box from "@mui/material/Box";
import TextField, { type TextFieldProps } from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { FormField } from "./FormField";

export type FormTextFieldProps = {
  name: string;
  label?: ReactNode;
  helperText?: ReactNode;
  rules?: ControllerProps["rules"];
  /** Hides the label asterisk while keeping `required` on the input. */
  hideRequiredMarker?: boolean;
  /**
   * When `true`, renders the field as a read-only label + value + pencil
   * affordance by default; clicking the row swaps in the editable
   * `TextField`, and `onBlur` flips back. Useful for "review" surfaces where
   * the data is already known and edits should feel like an exception, not
   * the default action.
   */
  editable?: boolean;
} & Omit<
  TextFieldProps,
  "name" | "value" | "onChange" | "error" | "helperText" | "label"
>;

export const FormTextField = ({
  name,
  label,
  helperText,
  required,
  hideRequiredMarker,
  rules,
  editable,
  ...rest
}: FormTextFieldProps) => {
  const { control } = useFormContext();
  const [isEditing, setIsEditing] = useState(false);
  const showDisplay = editable && !isEditing;

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field: { ref, ...field }, fieldState }) => {
        const value = field.value ?? "";
        return (
          <FormField
            label={label}
            helperText={fieldState.error?.message ?? helperText}
            error={!!fieldState.error}
            required={required}
            hideRequiredMarker={hideRequiredMarker}
            htmlFor={name}
          >
            {showDisplay ? (
              <Box
                component="button"
                type="button"
                ref={ref}
                onClick={() => setIsEditing(true)}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  cursor: "pointer",
                  background: "none",
                  border: 0,
                  padding: 0,
                  textAlign: "left",
                  color: "inherit",
                  alignSelf: "flex-start",
                }}
              >
                <Typography
                  variant="body1"
                  color={value ? "text.primary" : "text.secondary"}
                >
                  {value || rest.placeholder || ""}
                </Typography>
                <EditOutlinedIcon fontSize="small" color="primary" />
              </Box>
            ) : (
              <TextField
                id={name}
                autoFocus={editable && isEditing}
                {...rest}
                {...field}
                inputRef={ref}
                value={value}
                onBlur={(event) => {
                  field.onBlur();
                  if (editable) setIsEditing(false);
                  rest.onBlur?.(event);
                }}
                error={!!fieldState.error}
                required={required}
              />
            )}
          </FormField>
        );
      }}
    />
  );
};
